import { useCallback, useEffect, useState } from 'react';
import { CheckCell } from '@/pages/fulfillment/components/CheckCell';
import { Alert, Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import type { FulfillmentAdminClient, SampleRule } from '@/infrastructure/FulfillmentAdminClient';
import { describeFailure } from '@/utils/fulfillmentErrors';
import {
  isSampleWindowCancellable,
  newestSampleWindowsFirst,
  sampleWindowStatusLabel,
  sampleWindowStatusVariant,
} from '@/utils/fulfillmentMarketing';
import { kstDay, kstTimestamp } from '@/utils/fulfillmentTime';
import { showGlobalToast } from '@/pages/fulfillment/components/gtoast';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import '../../ultimateDesign.css';

interface CancelSampleAssignmentModalProps {
  readonly show: boolean;
  readonly client: FulfillmentAdminClient;
  readonly onHide: () => void;
  readonly onCancelled: () => void;
}

/** A window with no end runs forever, which is not the same as one whose end is merely unknown. */
const describePeriod = (rule: SampleRule): string =>
  `${kstTimestamp(rule.activeFrom)} → ${rule.activeTo === null ? 'forever' : kstTimestamp(rule.activeTo)}`;

const describeTarget = (rule: SampleRule): string =>
  rule.target === 'SELECTED' ? `${rule.orderIds?.length ?? 0} selected orders` : 'All new orders';

/**
 * Lists the assignment periods, newest first, and closes the ones an operator picks [wireframe M3]. A
 * scheduled period can be called off before it opens; an ended or cancelled one stays on the list for
 * the record but cannot be cancelled, and cancelling goes through a confirm step because it cannot be
 * undone for that period [WF-17].
 */
export function CancelSampleAssignmentModal({ show, client, onHide, onCancelled }: CancelSampleAssignmentModalProps) {
  const [rules, setRules] = useState<SampleRule[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await client.listSampleRules();
      setRules(newestSampleWindowsFirst(response.rules));
    } catch {
      setError('Could not load the assignment periods.');
    }
  }, [client]);

  useEffect(() => {
    if (show) {
      void load();
    }
  }, [show, load]);

  const toggle = (ruleKey: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(ruleKey)) {
        next.delete(ruleKey);
      } else {
        next.add(ruleKey);
      }
      return next;
    });

  const cancelPicked = async () => {
    setBusy(true);
    setError(null);
    let cancelled = 0;
    try {
      for (const ruleKey of picked) {
        await client.cancelSampleRule(ruleKey);
        cancelled += 1;
      }
      setPicked(new Set());
      setConfirming(false);
      showGlobalToast(`✓ ${cancelled} assignment period(s) cancelled`, {
        subtext: 'New assignments stopped for the selected period · already-assigned orders kept',
      });
      onCancelled();
      await load();
    } catch (failure) {
      // The server names the period and why — already cancelled, already ended — so its words are shown.
      const reason = describeFailure(failure, 'The server refused the cancellation.');
      setError(`Cancelled ${cancelled} of ${picked.size} period(s). ${reason}`);
      setPicked(new Set());
      setConfirming(false);
      if (cancelled > 0) {
        onCancelled();
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal show={show && !confirming} onHide={onHide} size="lg" {...ULTIMATE_MODAL}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Sample Assignment — Current Assignment Periods</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Table className="align-middle mb-0" size="sm">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th>Assignment Period</th>
                <th>Target</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.ruleKey} className={isSampleWindowCancellable(rule) ? undefined : 'text-muted'}>
                  <CheckCell>
                    <Form.Check
                      type="checkbox"
                      aria-label={`Select period ${rule.ruleKey}`}
                      disabled={!isSampleWindowCancellable(rule)}
                      checked={picked.has(rule.ruleKey)}
                      onChange={() => toggle(rule.ruleKey)}
                    />
                  </CheckCell>
                  <td className="font-monospace">{describePeriod(rule)}</td>
                  <td>{describeTarget(rule)}</td>
                  <td>
                    <Badge bg={sampleWindowStatusVariant(rule.status)}>{sampleWindowStatusLabel(rule.status)}</Badge>
                    {/* A window someone stopped and one that simply ran out both read as inactive — only this says which. */}
                    {rule.cancelledBy && (
                      <div className="small text-muted mt-1">
                        by {rule.cancelledBy}
                        {rule.cancelledAt ? ` · ${kstDay(rule.cancelledAt)}` : ''}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No assignment period has been opened.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <Alert variant="info" className="mt-3 mb-0 small">
            Select the period(s) to cancel, then confirm. A scheduled period can be cancelled before it opens. Ended and
            cancelled periods are kept for the record and cannot be cancelled. Cancelling stops new assignments for that
            period immediately; orders already assigned keep their sample set.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
          <Button variant="danger" disabled={picked.size === 0} onClick={() => setConfirming(true)}>
            Cancel Selected Periods
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirming} onHide={() => setConfirming(false)} {...ULTIMATE_MODAL}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel {picked.size} assignment period(s)?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-secondary">
          New assignments stop immediately. Orders already assigned keep their sample set.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirming(false)}>
            Keep periods
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => void cancelPicked()}>
            Cancel periods
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
